import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Call,
  CheckCircle,
  DeleteOutline,
  Email,
  EventAvailable,
  Groups,
  StickyNote2,
  Send,
  WhatsApp,
  Forum,
} from '@mui/icons-material';
import {
  ACTIVITY_OUTCOMES,
  ACTIVITY_TYPES,
  findOption,
  getFollowUpState,
  newActivityId,
} from './activityConfig';
import CopyButton from '../CopyButton';

const TYPE_ICONS = {
  note: StickyNote2,
  call: Call,
  email: Email,
  whatsapp: WhatsApp,
  meeting: Groups,
};

// One-click starters for the most common entries
const QUICK_ENTRIES = [
  { type: 'email', text: 'Sent introduction email', outcome: '' },
  { type: 'call', text: 'Called – no answer', outcome: 'no_response' },
  { type: 'call', text: 'Spoke on call – interested, asked to follow up', outcome: 'positive' },
  { type: 'whatsapp', text: 'Sent WhatsApp message', outcome: '' },
  { type: 'email', text: 'Followed up by email – no reply yet', outcome: 'no_response' },
];

const toDateInput = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dayLabel = (iso) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

const ActivityTimeline = ({ activities = [], nextFollowUpAt, currentUser, onAdd, onDelete, onSetFollowUp, saving }) => {
  const [type, setType] = useState('note');
  const [text, setText] = useState('');
  const [outcome, setOutcome] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const listRef = useRef(null);

  const sorted = useMemo(
    () => [...activities].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    [activities]
  );

  // Conversation style: keep the newest entry in view
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [sorted.length]);

  const followUpState = getFollowUpState(nextFollowUpAt);

  const reset = () => {
    setText('');
    setOutcome('');
    setFollowUp('');
    setType('note');
  };

  const submit = async () => {
    if (!text.trim() || saving) return;
    const entry = {
      id: newActivityId(),
      type,
      text: text.trim(),
      outcome: outcome || null,
      createdAt: new Date().toISOString(),
      authorId: currentUser?.id || '',
      authorName: currentUser?.name || 'Unknown user',
      followUpAt: followUp ? new Date(`${followUp}T09:00:00`).toISOString() : null,
    };
    const ok = await onAdd(entry);
    if (ok) reset();
  };

  const applyQuick = (quick) => {
    setType(quick.type);
    setText(quick.text);
    setOutcome(quick.outcome);
  };

  let lastDay = null;

  return (
    <Paper sx={{ borderRadius: 3, border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2, borderBottom: '1px solid #f1f5f9' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Forum sx={{ color: '#3b82f6' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
            Activity & follow-ups
          </Typography>
          <Chip size="small" label={activities.length} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
        </Stack>
      </Stack>

      {/* Next follow-up banner */}
      {followUpState && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ px: 2.5, py: 1.25, bgcolor: `${followUpState.color}12`, borderBottom: `1px solid ${followUpState.color}33` }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
            <EventAvailable sx={{ color: followUpState.color, fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
              Next follow-up: <span style={{ color: followUpState.color }}>{followUpState.label}</span>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              type="date"
              size="small"
              value={toDateInput(nextFollowUpAt)}
              onChange={(e) => e.target.value && onSetFollowUp(new Date(`${e.target.value}T09:00:00`).toISOString())}
              inputProps={{ 'aria-label': 'Change follow-up date' }}
              sx={{ '& input': { py: 0.5, fontSize: '0.8rem' }, bgcolor: '#fff' }}
            />
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<CheckCircle />}
              disabled={saving}
              onClick={() => onSetFollowUp(null, true)}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, whiteSpace: 'nowrap' }}
            >
              Mark done
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Conversation */}
      <Box ref={listRef} sx={{ flex: 1, minHeight: 220, maxHeight: 460, overflowY: 'auto', px: 2.5, py: 2, bgcolor: '#fbfcfe' }}>
        {sorted.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 180, textAlign: 'center', color: '#94a3b8' }}>
            <Forum sx={{ fontSize: 40, mb: 1, color: '#cbd5e1' }} />
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
              No activity yet
            </Typography>
            <Typography variant="caption">Log calls, emails and notes below to build a history for this contact.</Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {sorted.map((activity) => {
              const typeOption = findOption(ACTIVITY_TYPES, activity.type) || ACTIVITY_TYPES[0];
              const outcomeOption = findOption(ACTIVITY_OUTCOMES, activity.outcome);
              const Icon = TYPE_ICONS[typeOption.value] || StickyNote2;
              const isMine = currentUser?.id && activity.authorId === currentUser.id;
              const day = dayLabel(activity.createdAt);
              const showDay = day !== lastDay;
              lastDay = day;

              return (
                <React.Fragment key={activity.id}>
                  {showDay && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0' }} />
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>{day}</Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0' }} />
                    </Stack>
                  )}
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ '&:hover .activity-actions': { opacity: 1 } }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700, bgcolor: isMine ? '#dbeafe' : '#f1f5f9', color: isMine ? '#1d4ed8' : '#475569' }}>
                      {initials(activity.authorName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {activity.authorName || 'Unknown user'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {new Date(activity.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </Typography>
                        <Chip
                          size="small"
                          icon={<Icon sx={{ fontSize: '14px !important', color: `${typeOption.color} !important` }} />}
                          label={typeOption.label}
                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: `${typeOption.color}14`, color: '#334155' }}
                        />
                        {outcomeOption && (
                          <Chip
                            size="small"
                            label={outcomeOption.label}
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: `${outcomeOption.color}1a`, color: outcomeOption.color }}
                          />
                        )}
                      </Stack>
                      <Box
                        sx={{
                          mt: 0.5,
                          px: 1.5,
                          py: 1,
                          bgcolor: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px 12px 12px 12px',
                          position: 'relative',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word', pr: 6 }}>
                          {activity.text}
                        </Typography>
                        {activity.followUpAt && (
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: '#3b82f6', fontWeight: 600 }}>
                            <EventAvailable sx={{ fontSize: 14 }} />
                            Follow-up set for {new Date(activity.followUpAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </Typography>
                        )}
                        <Stack direction="row" className="activity-actions" sx={{ position: 'absolute', top: 4, right: 4, opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s' }}>
                          <CopyButton value={activity.text} label="Copy note" size={14} />
                          {pendingDelete === activity.id ? (
                            <Button size="small" color="error" onClick={() => { onDelete(activity.id); setPendingDelete(null); }} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: '0.7rem', textTransform: 'none' }}>
                              Delete?
                            </Button>
                          ) : (
                            <Tooltip title="Delete entry" placement="top">
                              <IconButton size="small" onClick={() => setPendingDelete(activity.id)} sx={{ p: 0.4, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                <DeleteOutline sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Stack>
                </React.Fragment>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Composer */}
      <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #f1f5f9', bgcolor: '#fff' }}>
        <Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
          {ACTIVITY_TYPES.map((option) => {
            const Icon = TYPE_ICONS[option.value];
            const selected = type === option.value;
            return (
              <Chip
                key={option.value}
                clickable
                size="small"
                onClick={() => setType(option.value)}
                icon={<Icon sx={{ fontSize: '16px !important', color: `${selected ? '#fff' : option.color} !important` }} />}
                label={option.label}
                aria-pressed={selected}
                sx={{
                  fontWeight: 600,
                  bgcolor: selected ? option.color : '#f8fafc',
                  color: selected ? '#fff' : '#475569',
                  border: `1px solid ${selected ? option.color : '#e2e8f0'}`,
                  '&:hover': { bgcolor: selected ? option.color : '#f1f5f9' },
                }}
              />
            );
          })}
        </Stack>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          placeholder="What happened? e.g. Called Priya, asked for a quote by Friday…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
        />

        {!text && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
            {QUICK_ENTRIES.map((quick) => (
              <Chip
                key={quick.text}
                size="small"
                variant="outlined"
                clickable
                label={quick.text}
                onClick={() => applyQuick(quick)}
                sx={{ fontSize: '0.72rem', borderColor: '#e2e8f0', color: '#64748b', borderStyle: 'dashed' }}
              />
            ))}
          </Stack>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1.5 }}>
          <TextField
            select
            size="small"
            label="Outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {ACTIVITY_OUTCOMES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: o.color, display: 'inline-block', mr: 1 }} />
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            size="small"
            label="Next follow-up"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: toDateInput(new Date()) }}
            sx={{ minWidth: 170 }}
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Ctrl / ⌘ + Enter">
            <span>
              <Button
                variant="contained"
                disableElevation
                endIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Send />}
                onClick={submit}
                disabled={!text.trim() || saving}
                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, width: { xs: '100%', sm: 'auto' } }}
              >
                Add to timeline
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Paper>
  );
};

export default ActivityTimeline;
